import {
  collection,
  query,
  limit,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  where,
  orderBy,
  serverTimestamp,
  getDoc,
  increment,
  runTransaction,
  writeBatch,
} from "firebase/firestore";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { db, auth, COLLECTIONS } from "../config/firebase";
import AuthService from "./authService";
import { ClubService } from "./clubService";
import { getEventEndTime, futureEventsCutoffISO, selectVisibleEvents } from "../utils/eventHelpers";
import { EVENT_CHAT_ENABLED } from "../constants/featureFlags";
import { fetchDocsByIds } from "./firestoreBatch";

// Default query limit to prevent unbounded Firestore reads.
// University of Miami has ~150 clubs and typically <200 active events.
// Callers needing all results can pass filters.limit with a higher value.
const DEFAULT_QUERY_LIMIT = 200;

export class EventService {
  // Get all events with proper visibility logic
  // options.userClubIds: string[] — optional. When provided, skips the
  // internal ClubService.getUserClubs() fetch and uses this list directly
  // for the private-event visibility check below. Pass it when the caller
  // already has the current user's club memberships in scope (e.g.
  // DataContext's userClubs) to avoid a redundant fetch on every call
  // (KAN-63). The not-authenticated → empty-result gate always runs — fixed
  // post-review: it used to live only in the else branch, so passing
  // userClubIds skipped the auth check entirely, not just the getUserClubs
  // fetch. Perf plan P2 (2026-09-23): that gate now reads auth.currentUser
  // directly instead of AuthService.getCurrentUser(), which ALSO fetched
  // users/{uid} from Firestore — a profile this method never reads (only
  // `uid` is used) — so every caller paid a round trip per call, and
  // ProfileScreen's Club Events tab paid it once PER CLUB. The verified-claim
  // token sync getCurrentUser() performed is kept explicitly below.
  static async getEvents(filters = {}, options = {}) {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { data: [], error: null };
      }
      await AuthService.ensureVerifiedClaimIsCurrent(user);

      let q = query(collection(db, COLLECTIONS.EVENTS));

      // Equality filters; the future-only range below adds an index on the
      // club path (events(club_id, start_date)).
      if (filters.clubId) {
        q = query(q, where("club_id", "==", filters.clubId));
      }
      if (filters.category) {
        q = query(q, where("category", "==", filters.category));
      }

      // Future-only: range + orderBy on start_date (ISO string) so a capped
      // query never drops upcoming events and skips past ones (BUG-PATTERNS
      // L35). Club-scoped uses the events(club_id, start_date) composite index
      // (TF232); the category path (no caller today) keeps the client filter.
      if (filters.futureOnly && !filters.category) {
        q = query(
          q,
          where("start_date", ">=", futureEventsCutoffISO()),
          orderBy("start_date")
        );
      }

      // Apply query limit (L4: prevent unbounded reads)
      const queryLimit = filters.limit || DEFAULT_QUERY_LIMIT;
      q = query(q, limit(queryLimit));

      // Membership ids resolve concurrently with the events query: a caller
      // array, a caller promise (options.userClubIdsPromise, e.g. a pending
      // loadUserClubs), or our own lookup. A failed lookup is an error, not
      // "member of nothing" (that would silently hide every private event).
      // ensureVerifiedClaimIsCurrent stays BEFORE the query (token must be
      // fresh for rules), so only the membership lookup overlaps.
      const idsPromise = Array.isArray(options.userClubIds)
        ? options.userClubIds
        : options.userClubIdsPromise ||
          ClubService.getUserClubs(user.uid).then(({ data, error: clubsError }) => {
            if (clubsError) throw clubsError;
            return data?.map((club) => club.id) || [];
          });
      const [querySnapshot, userClubIds] = await Promise.all([getDocs(q), idsPromise]);
      // Future-only + public-or-my-clubs + shape + soonest-first (eventHelpers).
      const events = selectVisibleEvents(
        querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
        {
          futureOnly: !!filters.futureOnly,
          canSeePrivate: (e) => userClubIds.includes(e.club_id),
        }
      );

      // Apply search filter after fetching (Firestore doesn't support full-text search)
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const filteredEvents = events.filter(
          (event) =>
            event.title.toLowerCase().includes(searchTerm) ||
            event.description?.toLowerCase().includes(searchTerm)
        );
        return { data: filteredEvents, error: null };
      }

      return { data: events, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get events error:", error);
      return { data: null, error };
    }
  }

  // Events of several clubs the caller belongs to: one club_id query per
  // club, in parallel, each capped at DEFAULT_QUERY_LIMIT (a shared cap across
  // an `in` chunk could drop upcoming events of busy clubs). Member clubs =>
  // private events are visible. Same shape/filter/sort as getEvents. One
  // failed club keeps the others' events (error only if every query failed).
  static async getEventsForClubs(clubIds, { futureOnly = true } = {}) {
    try {
      const user = auth.currentUser;
      if (!user) return { data: [], error: null };
      const ids = [...new Set(clubIds || [])].filter(Boolean);
      const settled = await Promise.allSettled(
        ids.map((clubId) =>
          getDocs(
            query(
              collection(db, COLLECTIONS.EVENTS),
              where("club_id", "==", clubId),
              ...(futureOnly
                ? [where("start_date", ">=", futureEventsCutoffISO()), orderBy("start_date")]
                : []),
              limit(DEFAULT_QUERY_LIMIT)
            )
          )
        )
      );
      const snapshots = settled.filter((r) => r.status === "fulfilled").map((r) => r.value);
      if (ids.length > 0 && snapshots.length === 0) throw settled[0].reason;
      // Deduped by id (canSeePrivate: these are the caller's own clubs).
      const byId = new Map();
      snapshots.forEach((snap) => snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() })));
      return {
        data: selectVisibleEvents([...byId.values()], { futureOnly, canSeePrivate: () => true }),
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Get events for clubs error:", error);
      return { data: null, error };
    }
  }

  // Get event by ID
  // options.club: optional already-loaded club; reused instead of re-reading
  // the club doc only when its id matches the event's club_id AND it carries
  // every field returned below (id, name, logo_url, description).
  // A missing event returns { data: null, error, notFound: true }.
  static async getEventById(eventId, options = {}) {
    try {
      const eventDoc = await getDoc(doc(db, COLLECTIONS.EVENTS, eventId));

      if (!eventDoc.exists()) {
        return { data: null, error: "Event not found", notFound: true };
      }

      const eventData = eventDoc.data();

      // Get club info
      let clubData;
      const known = options.club;
      if (known && known.id === eventData.club_id && known.description !== undefined) {
        clubData = known;
      } else {
        const clubDoc = await getDoc(
          doc(db, COLLECTIONS.CLUBS, eventData.club_id)
        );
        clubData = clubDoc.exists() ? clubDoc.data() : null;
      }

      return {
        data: {
          id: eventDoc.id,
          ...eventData,
          club: clubData
            ? {
                id: clubData.id,
                name: clubData.name,
                logo_url: clubData.logo_url,
                description: clubData.description,
              }
            : null,
        },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Get event by ID error:", error);
      return { data: null, error };
    }
  }

  // Create new event.
  // options.skipMembershipCheck (perf P5, 2026-09-23): createRecurringEvents
  // verifies membership up front and then every RECHECK_EVERY occurrences,
  // passing true for the occurrences in between — the check is identical for
  // every occurrence of one series, and repeating it was a full round trip
  // per occurrence (52 extra for a weekly year). Every other caller leaves it
  // false and gets the check exactly as before.
  // `eventId`: optional pre-generated doc id (e.g. `doc(collection(db,
  // COLLECTIONS.EVENTS)).id` computed by the caller before this call) so a
  // Storage upload keyed on that id (event-images/<eventId>) lands under
  // the SAME id this write creates the event doc at, instead of the
  // caller having to upload only after learning the auto-generated id.
  static async createEvent(
    eventData,
    creatorId,
    { skipMembershipCheck = false, eventId = null } = {}
  ) {
    try {
      if (!creatorId) {
        throw new Error("creatorId is required but was not provided");
      }

      // H1: Verify creator is a member of the target club
      if (eventData.club_id && !skipMembershipCheck) {
        const memberQuery = query(
          collection(db, COLLECTIONS.CLUB_MEMBERS),
          where("club_id", "==", eventData.club_id),
          where("user_id", "==", creatorId)
        );
        const memberSnapshot = await getDocs(memberQuery);
        if (memberSnapshot.empty) {
          return { data: null, error: { message: "Unauthorized: you must be a club member to create events" } };
        }
      }

      // H8: Field allowlist -- reject arbitrary fields from eventData
      const ALLOWED_EVENT_FIELDS = [
        'title',
        'description',
        'club_id',
        'start_date',
        'end_date',
        'location',
        'is_public',
        'image_url',
        'category',
        'qr_checkin_enabled', // legacy (TF227): always written true; the server ignores it
        'recurrence_group_id', // KAN-35: shared id tagging occurrences generated by one createRecurringEvents call
      ];

      const sanitized = {};
      for (const key of Object.keys(eventData)) {
        if (ALLOWED_EVENT_FIELDS.includes(key)) {
          sanitized[key] = eventData[key];
        }
      }

      if (__DEV__) {
        const droppedKeys = Object.keys(eventData).filter(
          (key) => !ALLOWED_EVENT_FIELDS.includes(key)
        );
        if (droppedKeys.length > 0) {
          console.error(
            "[EventService.createEvent] Dropped unrecognized field(s) not in ALLOWED_EVENT_FIELDS:",
            droppedKeys
          );
        }
      }

      const eventDoc = {
        ...sanitized,
        creator_id: creatorId,
        is_public: sanitized.is_public !== false, // Default to public
        qr_checkin_enabled: sanitized.qr_checkin_enabled !== false, // legacy field, server ignores it (TF227)
        is_active: true,
        attendee_count: 0, // Initialize attendee count
        checkIn_count: 0, // Initialize check-in count
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };

      // Perf P5 (2026-09-23): one atomic batch — event doc + the club's
      // event_count in a single round trip — instead of addDoc followed by
      // ClubService.updateClubEventCount's read-then-write transaction (3
      // sequential round trips, and the transaction could abort-retry under
      // contention). increment(1) keeps the write's delta at exactly +1, which
      // is what firestore.rules' clubEventCountChangeIsSafe bounds (±1 per
      // write, event_count-only diff) — proven by
      // firebase-functions/test/rules/eventCountBatch.test.js. The decrement
      // path (deleteEvent → updateClubEventCount(-1)) is unchanged. A rules
      // rejection now fails the whole batch (no event without its count)
      // where the old path silently swallowed a failed count update.
      const docRef = eventId
        ? doc(db, COLLECTIONS.EVENTS, eventId)
        : doc(collection(db, COLLECTIONS.EVENTS));
      const batch = writeBatch(db);
      batch.set(docRef, eventDoc);
      if (sanitized.club_id) {
        batch.update(doc(db, COLLECTIONS.CLUBS, sanitized.club_id), {
          event_count: increment(1),
        });
      }
      await batch.commit();

      return { data: { id: docRef.id, ...eventDoc }, error: null };
    } catch (error) {
      if (__DEV__) console.error("Create event error:", error);
      return { data: null, error };
    }
  }

  // KAN-35: recurring events. Generates `occurrences` independent event
  // docs, one createEvent() call per occurrence -- the same membership
  // check, field allowlist, and club-count increment as a single event
  // apply to each, since nothing here is a new trust boundary (an admin
  // creating N events for their own club is exactly as trusted as creating
  // one). Each occurrence is a normal, fully independent event afterward:
  // editing or deleting one never touches the others.
  // `recurrence_group_id` is informational only (no feature currently reads
  // it back) -- it exists purely so a future support/debugging pass can
  // trace which events came from one series.
  //
  // baseEventData must NOT include start_date -- pass the first occurrence's
  // start time as `firstStartDate` (a Date); every later occurrence is
  // computed from it.
  //
  // Perf P5 (2026-09-23): the membership check runs up front and then every
  // RECHECK_EVERY occurrences (it is the same query for every occurrence),
  // and each occurrence is one batched write (event doc + event_count:
  // increment(1) — firestore.rules bounds event_count to ±1 per write, so a
  // series can never be one increment(N)).
  // Occurrences are still written SEQUENTIALLY, on purpose: a parallel
  // version was reviewed and rejected (Codex, 2026-09-23) because a failure
  // in the middle of a parallel chunk leaves a NON-PREFIX partial series
  // (weeks 1, 3, 4 created, week 2 missing) that the "Created X of N before
  // failing" contract can't describe, and it adds concurrent writes to the
  // single club doc. Sequential keeps the original prefix-consistent
  // semantics. A weekly-for-a-year series went from ~208 round trips
  // (membership + addDoc + 2-step transaction per occurrence) to ~58
  // (1 check + 52 batch commits + 5 periodic re-checks).
  static async createRecurringEvents(
    baseEventData,
    creatorId,
    { firstStartDate, frequency, occurrences }
  ) {
    const MAX_OCCURRENCES = 52; // weekly for a year -- a sane ceiling against a fat-fingered count
    const count = Math.min(
      Math.max(parseInt(occurrences, 10) || 0, 1),
      MAX_OCCURRENCES
    );
    if (!(firstStartDate instanceof Date) || isNaN(firstStartDate.getTime())) {
      return {
        data: null,
        error: { message: "firstStartDate must be a valid Date" },
      };
    }

    // H1 (hoisted): verify membership once for the whole series. Same query
    // and same failure message createEvent uses for a single event.
    if (baseEventData.club_id) {
      try {
        const memberSnapshot = await getDocs(
          query(
            collection(db, COLLECTIONS.CLUB_MEMBERS),
            where("club_id", "==", baseEventData.club_id),
            where("user_id", "==", creatorId)
          )
        );
        if (memberSnapshot.empty) {
          return {
            data: [],
            error: { message: "Unauthorized: you must be a club member to create events" },
          };
        }
      } catch (error) {
        if (__DEV__) console.error("Recurring events membership check error:", error);
        return { data: [], error };
      }
    }

    // Client-generated -- collision odds are negligible (uid + exact ms
    // timestamp) and nothing enforces uniqueness server-side; this is a
    // traceability tag, not an identity key.
    const recurrenceGroupId = `${creatorId}_${firstStartDate.getTime()}`;

    const occurrenceDates = [];
    for (let i = 0; i < count; i++) {
      const occurrenceDate = new Date(firstStartDate);
      if (frequency === "biweekly") {
        occurrenceDate.setDate(occurrenceDate.getDate() + i * 14);
      } else if (frequency === "monthly") {
        // Naive month arithmetic: a start date on the 29th-31st can land on
        // a different day for short months (JS Date rolls the overflow
        // into the following month rather than clamping). Acceptable for
        // recurring meetings, which are overwhelmingly scheduled on a fixed
        // weekday, not a fixed day-of-month.
        occurrenceDate.setMonth(occurrenceDate.getMonth() + i);
      } else {
        occurrenceDate.setDate(occurrenceDate.getDate() + i * 7); // weekly (default)
      }
      occurrenceDates.push(occurrenceDate);
    }

    // Codex round 2 (2026-09-23): a single hoisted check leaves a window
    // where a member removed from the club mid-series would still get the
    // remaining occurrences written (the membership check is service-layer
    // only — firestore.rules' events create is isAuth()). Re-checking every
    // RECHECK_EVERY occurrences bounds that window to a few writes for ~5
    // extra round trips on a year-long series, instead of the 52 the old
    // per-occurrence check cost.
    const RECHECK_EVERY = 10;
    const created = [];
    for (let i = 0; i < occurrenceDates.length; i++) {
      const occurrenceDate = occurrenceDates[i];
      const recheckMembership = i > 0 && i % RECHECK_EVERY === 0;
      const { data, error } = await this.createEvent(
        {
          ...baseEventData,
          start_date: occurrenceDate.toISOString(),
          recurrence_group_id: recurrenceGroupId,
        },
        creatorId,
        { skipMembershipCheck: !recheckMembership }
      );

      if (error) {
        // No automatic rollback -- each occurrence already created is a
        // real, independent event other users may already be seeing.
        // Surface what succeeded so the caller can tell the admin exactly
        // where the series stopped, rather than silently losing track.
        // Sequential writes guarantee `created` is exactly the leading
        // prefix of the series, so this count is precise.
        return {
          data: created,
          error: {
            message: `Created ${created.length} of ${count} occurrences before failing: ${error.message || error}`,
          },
        };
      }
      created.push(data);
    }

    return { data: created, error: null };
  }

  // Update event
  static async updateEvent(eventId, updates, userId) {
    try {
      // H9: userId is REQUIRED -- return error if falsy (not silently skip auth)
      if (!userId) {
        return { data: null, error: { message: "userId is required for authorization" } };
      }

      const eventRef = doc(db, COLLECTIONS.EVENTS, eventId);
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        return { data: null, error: { message: "Event not found" } };
      }
      const eventData = eventDoc.data();
      const isCreator = eventData.creator_id === userId;

      // SP-6 perf: short-circuit when the caller is already the creator.
      // Auth correctness is preserved — creators were always allowed; only
      // non-creators have to pay for the admin + super-admin round trips.
      if (!isCreator) {
        // Run the club-admin lookup and the super-admin claim check concurrently
        // — they're independent, so parallelizing halves the auth round-trips for
        // the non-creator edit path (D5 latency target).
        // club-eboard-permission-parity: e-board can edit an event too.
        const adminCheckPromise = eventData.club_id
          ? getDocs(
              query(
                collection(db, COLLECTIONS.CLUB_MEMBERS),
                where("club_id", "==", eventData.club_id),
                where("user_id", "==", userId),
                where("role", "in", ["admin", "e-board"])
              )
            )
              .then((snap) => !snap.empty)
              .catch(() => false)
          : Promise.resolve(false);

        const superAdminCheckPromise = AuthService.getCurrentUser()
          .then(async ({ user }) => {
            if (!user) return false;
            const tokenResult = await user.getIdTokenResult();
            return tokenResult.claims.superAdmin === true;
          })
          .catch(() => false);

        const [isClubAdmin, isSuperAdmin] = await Promise.all([
          adminCheckPromise,
          superAdminCheckPromise,
        ]);

        if (!isClubAdmin && !isSuperAdmin) {
          return { data: null, error: { message: "Unauthorized" } };
        }
      }

      // H6: Field allowlist for updates -- prevent mass-assignment
      const ALLOWED_UPDATE_FIELDS = [
        'title',
        'description',
        'start_date',
        'end_date',
        'location',
        'is_public',
        'image_url',
        'category',
        'is_active',
        'qr_checkin_enabled', // legacy (TF227): always written true; the server ignores it
        // NOTE: reminder_sent is intentionally NOT here. It's a server-managed
        // flag (written only by the eventStartingSoonReminder Cloud Function
        // via the Admin SDK) and is now pinned in firestore.rules
        // (eventServerFieldsUnchanged) so a client write of it is rejected —
        // a client update including it would be denied WHOLESALE. It had no
        // live caller (the "reopen a past event re-arms the reminder"
        // capability was never wired to any UI); if that feature is ever
        // built, re-arm the flag from a Cloud Function, not the client.
      ];

      const sanitized = {};
      for (const key of Object.keys(updates)) {
        if (ALLOWED_UPDATE_FIELDS.includes(key)) {
          sanitized[key] = updates[key];
        }
      }

      if (__DEV__) {
        const droppedKeys = Object.keys(updates).filter(
          (key) => !ALLOWED_UPDATE_FIELDS.includes(key)
        );
        if (droppedKeys.length > 0) {
          console.error(
            "[EventService.updateEvent] Dropped unrecognized field(s) not in ALLOWED_UPDATE_FIELDS:",
            droppedKeys
          );
        }
      }

      if (Object.keys(sanitized).length === 0) {
        return { data: null, error: { message: "No valid fields to update" } };
      }

      await updateDoc(eventRef, {
        ...sanitized,
        updated_at: serverTimestamp(),
      });

      return { data: { id: eventId, ...sanitized }, error: null };
    } catch (error) {
      if (__DEV__) console.error("Update event error:", error);
      return { data: null, error };
    }
  }

  // Delete event
  static async deleteEvent(eventId, userId) {
    try {
      // H9: userId is REQUIRED -- return error if falsy (not silently skip auth)
      if (!userId) {
        return { error: { message: "userId is required for authorization" } };
      }

      // Get event data first to know the club_id
      const eventRef = doc(db, COLLECTIONS.EVENTS, eventId);
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        return { error: { message: "Event not found" } };
      }
      const eventData = eventDoc.data();

      // Authorization check (userId is always present at this point).
      // Deletion is restricted to the event's CREATOR or a super admin, to match
      // the firestore.rules events delete rule (creator_id == uid || superAdmin).
      // The previous "any club admin may delete" affordance was dropped when that
      // rule was tightened to close a mass-deletion hole (a blanket isAuth() let
      // any signed-in account delete every event in the app via the raw SDK). In
      // this app the club admin is effectively the club creator — the creator of
      // their own events — so a non-creator admin (e.g. after an admin transfer)
      // has a super admin delete on their behalf.
      const isCreator = eventData.creator_id === userId;

      // The club's creator may also delete any event in their club — this matches
      // the firestore.rules events delete rule (which authorizes on the club's
      // immutable creator_id) and covers events created by other club members.
      //
      // This and the super-admin claim check below are independent, so they run
      // concurrently via Promise.all — mirrors updateEvent's adminCheckPromise /
      // superAdminCheckPromise above (KAN-93 perf).
      const clubCreatorCheckPromise = eventData.club_id
        ? getDoc(doc(db, COLLECTIONS.CLUBS, eventData.club_id))
            .then(
              (clubDoc) =>
                clubDoc.exists() && clubDoc.data().creator_id === userId
            )
            .catch(() => false) // isClubCreator stays false on error — fail closed
        : Promise.resolve(false);

      // Check if user is a super admin via Firebase Custom Claims
      // (custom claims are server-set only — cannot be spoofed client-side)
      const superAdminCheckPromise = AuthService.getCurrentUser()
        .then(async ({ user }) => {
          if (!user) return false;
          const tokenResult = await user.getIdTokenResult();
          return tokenResult.claims.superAdmin === true;
        })
        .catch(() => false); // isSuperAdmin stays false on error — fail closed

      const [isClubCreator, isSuperAdmin] = await Promise.all([
        clubCreatorCheckPromise,
        superAdminCheckPromise,
      ]);

      if (!isCreator && !isClubCreator && !isSuperAdmin) {
        return { error: { message: "Unauthorized" } };
      }

      // Delete the event document. firestore.rules allows delete only if
      // creator_id matches or the caller is a super admin — this mirrors it.
      await deleteDoc(eventRef);

      // NOTE: Orphaned records in event_attendees, event_chats, event_messages,
      // and notifications are left in place. They reference a non-existent event_id
      // and are never surfaced by any query. Client-side cascading deletes were
      // removed because Firestore rules deny deleting other users' records.

      // Update club's event count
      if (eventData.club_id) {
        await ClubService.updateClubEventCount(eventData.club_id, -1);
      }

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Delete event error:", error);
      return { error };
    }
  }

  // RSVP to event
  static async rsvpToEvent(eventId, userId, status = "attending") {
    try {
      const eventRef = doc(db, COLLECTIONS.EVENTS, eventId);

      // DATA-02: Use a transaction to prevent duplicate RSVPs and count corruption
      await runTransaction(db, async (transaction) => {
        // Check for an existing RSVP within the transaction
        const existingQuery = query(
          collection(db, COLLECTIONS.EVENT_ATTENDEES),
          where("event_id", "==", eventId),
          where("user_id", "==", userId)
        );
        const existingSnapshot = await getDocs(existingQuery);

        if (!existingSnapshot.empty) {
          // Already has a record — update the status if different
          const existingDoc = existingSnapshot.docs[0];
          if (existingDoc.data().status !== status) {
            transaction.update(existingDoc.ref, { status, rsvp_at: serverTimestamp() });
          }
          return;
        }

        // No existing RSVP — create one and atomically increment the attendee count
        const newRsvpRef = doc(collection(db, COLLECTIONS.EVENT_ATTENDEES));
        transaction.set(newRsvpRef, {
          event_id: eventId,
          user_id: userId,
          status,
          rsvp_at: serverTimestamp(),
        });

        if (status === "attending") {
          transaction.update(eventRef, { attendee_count: increment(1) });
        }
      });

      // Event chat is feature-flagged OFF (constants/featureFlags.js); the
      // RSVP then returns as soon as the transaction commits (TF231: the
      // success pop-up used to wait on an event re-read + chat lookup/create
      // for the disabled feature). When on, it is awaited as before so callers
      // that fetch the chat right after see it.
      if (status === "attending" && EVENT_CHAT_ENABLED) {
        try {
          const eventDoc = await getDoc(eventRef);
          if (eventDoc.exists()) {
            await this.createEventChat(eventId, eventDoc.data().title || "Event");
          }
        } catch (chatError) {
          if (__DEV__) console.error("Error creating event chat:", chatError);
          // Don't fail the RSVP if chat creation fails
        }
      }

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("RSVP to event error:", error);
      return { error };
    }
  }

  // Cancel RSVP
  static async cancelRsvp(eventId, userId) {
    try {
      const eventRef = doc(db, COLLECTIONS.EVENTS, eventId);

      // M8: Use a transaction to atomically delete the attendee record and decrement the count
      await runTransaction(db, async (transaction) => {
        // Read the event document inside the transaction
        const eventDoc = await transaction.get(eventRef);
        if (!eventDoc.exists()) return;

        // Find the attendee record (query outside transaction read-set is acceptable;
        // worst case is a no-op if a concurrent cancel already deleted the record)
        const attendeesQuery = query(
          collection(db, COLLECTIONS.EVENT_ATTENDEES),
          where("event_id", "==", eventId),
          where("user_id", "==", userId)
        );
        const attendeesSnapshot = await getDocs(attendeesQuery);

        if (!attendeesSnapshot.empty) {
          // Delete attendee record
          transaction.delete(attendeesSnapshot.docs[0].ref);

          // Decrement count, guard against negative
          const currentCount = eventDoc.data().attendee_count || 0;
          transaction.update(eventRef, {
            attendee_count: Math.max(currentCount - 1, 0),
          });
        }
      });

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Cancel RSVP error:", error);
      return { error };
    }
  }

  // Get user's events
  static async getUserEvents(userId) {
    try {
      const attendeesQuery = query(
        collection(db, COLLECTIONS.EVENT_ATTENDEES),
        where("user_id", "==", userId)
      );
      const attendeesSnapshot = await getDocs(attendeesQuery);

      // Events, then their clubs, in chunked `documentId() in` queries
      // (services/firestoreBatch.js) instead of one getDoc per row (TF232).
      const attendeeDocs = attendeesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const eventDataById = await fetchDocsByIds(
        COLLECTIONS.EVENTS,
        attendeeDocs.map(a => a.event_id)
      );
      const eventMap = new Map();
      for (const [eventId, eventData] of eventDataById) {
        eventMap.set(eventId, { id: eventId, ...eventData });
      }
      const clubMap = await fetchDocsByIds(
        COLLECTIONS.CLUBS,
        [...eventMap.values()].map(e => e.club_id)
      );

      // Assemble attendances
      const attendances = [];
      for (const attendeeData of attendeeDocs) {
        const eventData = eventMap.get(attendeeData.event_id);
        if (!eventData) continue;

        const clubData = eventData.club_id ? clubMap.get(eventData.club_id) : null;

        attendances.push({
          id: attendeeData.id,
          event_id: attendeeData.event_id,
          user_id: attendeeData.user_id,
          status: attendeeData.status,
          checked_in: attendeeData.checked_in || false,
          created_at: attendeeData.created_at,
          event: {
            ...eventData,
            club: clubData
              ? {
                  id: eventData.club_id,
                  name: clubData.name,
                  logo_url: clubData.logo_url,
                }
              : null,
          },
        });
      }

      return { data: attendances, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get user events error:", error);
      return { data: null, error };
    }
  }

  // Get event attendees
  static async getEventAttendees(eventId, options = {}) {
    try {
      // options.limit caps the attendee rows (and therefore the profile
      // fetches) — the club-page avatar stack only needs a few.
      const attendeesQuery = options.limit
        ? query(
            collection(db, COLLECTIONS.EVENT_ATTENDEES),
            where("event_id", "==", eventId),
            where("status", "==", "attending"),
            limit(options.limit)
          )
        : query(
            collection(db, COLLECTIONS.EVENT_ATTENDEES),
            where("event_id", "==", eventId),
            where("status", "==", "attending")
          );
      const attendeesSnapshot = await getDocs(attendeesQuery);

      // Reuse profiles the admin screen already loaded with the club member
      // list. Only attendees missing from that cache require a fetch, and
      // those are batched into chunked `where(documentId(), 'in', chunk)`
      // queries (30 ids/query, same helper as ClubService.getClubMembers)
      // instead of one getDoc per attendee. The old per-user getDoc loop
      // was the reason events with 20+ RSVPs were slow to open — every
      // attendee cost its own Firestore round trip; now a whole event's
      // roster is typically one query.
      const knownUsersById = options.knownUsersById;
      const getKnownUser = (userId) =>
        knownUsersById?.get?.(userId) || knownUsersById?.[userId];
      const missingUserIds = attendeesSnapshot.docs
        .map((attendeeDoc) => attendeeDoc.data().user_id)
        .filter((userId) => !getKnownUser(userId));
      const fetchedUsersById = await ClubService._fetchUsersByIds(
        missingUserIds
      );

      // Deduplicate by user_id so each user only appears once. A user can
      // have more than one row (an RSVP plus a check-in shadow row); keep the
      // checked-in one so attendance is never under-reported (2026-10-11).
      const docByUserId = new Map();
      for (const attendeeDoc of attendeesSnapshot.docs) {
        const userId = attendeeDoc.data().user_id;
        const kept = docByUserId.get(userId);
        if (!kept || (!kept.data().checked_in && attendeeDoc.data().checked_in)) {
          docByUserId.set(userId, attendeeDoc);
        }
      }
      const attendees = [...docByUserId.values()]
        .map((attendeeDoc) => {
          const attendeeData = attendeeDoc.data();
          const userId = attendeeData.user_id;
          const userData =
            getKnownUser(userId) ||
            fetchedUsersById.get(userId) ||
            // Export keeps every attendance row: a profile that failed to load
            // must show up as a gap in the CSV, not silently vanish.
            (options.includeMissingProfiles ? { id: userId } : null);
          if (!userData) return null;
          return {
            id: attendeeDoc.id,
            ...attendeeData,
            user: {
              id: userData.id || userId,
              full_name: userData.full_name,
              email: userData.email,
              avatar_url: userData.avatar_url || userData.photoURL,
            },
          };
        })
        .filter(Boolean);

      return { data: attendees, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get event attendees error:", error);
      return { data: null, error };
    }
  }

  // Get user's event attendance
  static async getUserEventAttendance(eventId, userId) {
    try {
      const attendanceQuery = query(
        collection(db, COLLECTIONS.EVENT_ATTENDEES),
        where("event_id", "==", eventId),
        where("user_id", "==", userId),
        where("status", "==", "attending")
      );
      const attendanceSnapshot = await getDocs(attendanceQuery);

      const attendance = attendanceSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { data: attendance, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get user event attendance error:", error);
      return { data: null, error };
    }
  }

  // Get event chat - returns existing chat or null if doesn't exist
  static async getEventChat(eventId) {
    try {
      const chatQuery = query(
        collection(db, COLLECTIONS.EVENT_CHATS),
        where("event_id", "==", eventId)
      );
      const chatSnapshot = await getDocs(chatQuery);

      if (chatSnapshot.empty) {
        return { data: null, error: null };
      }

      const chatDoc = chatSnapshot.docs[0];
      return {
        data: {
          id: chatDoc.id,
          ...chatDoc.data(),
        },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Get event chat error:", error);
      return { data: null, error };
    }
  }

  // Create event chat - called when first attendee joins
  static async createEventChat(eventId, eventName) {
    try {
      // Check if chat already exists
      const { data: existingChat } = await this.getEventChat(eventId);
      if (existingChat) {
        return { data: existingChat, error: null };
      }

      // Create new event chat
      const chatData = {
        event_id: eventId,
        event_name: eventName,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };

      const chatRef = await addDoc(
        collection(db, COLLECTIONS.EVENT_CHATS),
        chatData
      );

      return {
        data: {
          id: chatRef.id,
          ...chatData,
        },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Create event chat error:", error);
      return { data: null, error };
    }
  }

  /**
   * Check if event is in the past. Delegates to eventHelpers'
   * getEventEndTime so the boundary matches the check-in window everywhere:
   * legacy end_date honored when present, otherwise start + the shared
   * EVENT_DEFAULT_DURATION_MS (4h). Previously this fell back to bare
   * start_date, which — once end dates were removed from the product
   * (2026-08-19) — made every new event "past" the instant it started and
   * dropped its chat from ChatsScreen mid-event (eventChatService filters
   * on this method).
   */
  static isEventPast(event) {
    return new Date() > getEventEndTime(event);
  }
}

// BUG-AUDIT-SEP23-BATCH2 item 4 (part 2) — "did this user tap Yes on the
// Google Calendar add prompt for this event" flag, so a later un-RSVP can
// ask whether to also remove it. Deliberately device-local (AsyncStorage),
// NOT a Firestore field on the event_attendees doc: that doc is HARD-deleted
// by cancelRsvp above the instant a user leaves an event, i.e. exactly when
// this flag would need to be read — a Firestore field would have to be read
// before its own deletion for no benefit over local storage, plus it would
// need a firestore.rules change to allow the client write. This mirrors
// utils/chatReadTracker.js's per-id AsyncStorage convention (key prefix,
// best-effort try/catch — a lost flag only means the removal prompt is
// skipped, never a correctness issue). Owner-approved "ask + hand off"
// scope, not true silent calendar removal (see BUG-AUDIT-SEP23-BATCH2-PLAN.md
// item 4).
const GCAL_ADDED_KEY_PREFIX = "gcal_added_";

export async function markEventAddedToGcal(eventId, userId) {
  if (!eventId || !userId) return;
  try {
    await AsyncStorage.setItem(`${GCAL_ADDED_KEY_PREFIX}${eventId}_${userId}`, "1");
  } catch {
    // Best-effort — worst case the removal prompt just doesn't fire later.
  }
}

export async function wasEventAddedToGcal(eventId, userId) {
  if (!eventId || !userId) return false;
  try {
    const value = await AsyncStorage.getItem(`${GCAL_ADDED_KEY_PREFIX}${eventId}_${userId}`);
    return value === "1";
  } catch {
    return false;
  }
}

export async function clearEventAddedToGcal(eventId, userId) {
  if (!eventId || !userId) return;
  try {
    await AsyncStorage.removeItem(`${GCAL_ADDED_KEY_PREFIX}${eventId}_${userId}`);
  } catch {
    // Best-effort.
  }
}
